import DataGrid, { DataGridConfig } from 'components/DataGrid';
import IconButton from 'components/IconButton';
import React, { useState } from 'react';
import { Card, CardHeader, CardBody, Row, Col } from 'reactstrap';
import { DEFAULT_PAGE_SIZE } from 'views/types';

import { ModalDialogData } from '../types';

// Define the dataset type for MultiViewChart
export type ChartDataSet = {
  dataset: number[];
  label: string;
  color: string;
  getDrillDownUrl: (label: string) => string;
};

// Props type for the MultiViewChart component
interface DashboardDataGridChartProps {
  data: unknown[];
  header: string;
  subHeader: string;
  dataGridConfig: Pick<DataGridConfig, 'rowIdKey' | 'columns'>;
  onExpandClick: (data: ModalDialogData | undefined) => void;
}

// Inline component for rendering the DataGrid with pagination
type TableViewProps = {
  pageSize?: number;
  rowsPerPageOptions?: number[];
};

const DashboardDataGridChart: React.FC<DashboardDataGridChartProps> = ({
  data,
  header,
  subHeader,
  dataGridConfig,
  onExpandClick,
}) => {
  const TableView: React.FC<TableViewProps> = ({
    pageSize = DEFAULT_PAGE_SIZE,
    rowsPerPageOptions = undefined,
  }) => {
    const [currentPage, setCurrentPage] = useState(0);
    const [currentPageSize, setCurrentPageSize] = useState(pageSize);

    const paginatedData = React.useMemo(
      () =>
        data.slice(
          currentPage * currentPageSize,
          (currentPage + 1) * currentPageSize,
        ),
      [data, currentPage, currentPageSize],
    );

    const getMergedConfig = React.useMemo(
      (): DataGridConfig => ({
        ...dataGridConfig, // Merge rowIdKey and columns from props
        paginationConfig: {
          total_count: data.length,
          pageNumber: currentPage,
          pageSize: currentPageSize,
          rowsPerPageOptions: rowsPerPageOptions,
          onPageChange: (newPage) => {
            setCurrentPage(newPage);
          },
          onPageSizeChange: (newPageSize) => {
            setCurrentPageSize(newPageSize);
            setCurrentPage(0);
          },
        },
      }),
      [
        dataGridConfig,
        data.length,
        currentPage,
        currentPageSize,
        rowsPerPageOptions,
      ],
    );

    return <DataGrid config={getMergedConfig} data={paginatedData} />;
  };

  // Function to render content based on the selected view
  const renderContent = () => {
    // fixed page size for the table view, and no pagination options
    return <TableView rowsPerPageOptions={[]} pageSize={7} />;
  };

  // Header for the chart card
  const chartHeader = (
    <CardHeader>
      <Row>
        <Col className="d-flex flex-row align-items-baseline">
          <h2 className="d-inline mb-0">{header}</h2>
          <h5 className="card-category px-1">{subHeader}</h5>
        </Col>
        <Col>
          <div className="d-inline float-right">
            <IconButton
              id="view_details"
              iconClass="bi bi-table"
              size="sm"
              cssClass="padding-2"
              tooltip="View Details"
              onClick={() => {
                if (data?.length > 0 && onExpandClick) {
                  onExpandClick({
                    title: `${subHeader} (${data.length})`,
                    subTitle: <span>{header}</span>,
                    bodyComponent: <TableView />,
                  });
                }
              }}
            />
          </div>
        </Col>
      </Row>
    </CardHeader>
  );

  return (
    <Card className="card-chart border border-light">
      {chartHeader}
      <CardBody className="pb-0">
        {/* Content area for the chart or table */}
        <div
          className="chart-area overflow-auto"
          style={{ overflowX: 'hidden' }}
        >
          {renderContent()}
        </div>
      </CardBody>
    </Card>
  );
};

export default DashboardDataGridChart;
